import React, {Component} from 'react'
import PropTypes from 'prop-types'
import DropdownTreeSelect from 'react-dropdown-tree-select'
import {isEqual, flatten, cloneDeep} from 'lodash'
import 'react-dropdown-tree-select/dist/styles.css'
import './TreeMultiselect.css'

const SPECIAL_NODE_SELECT_ALL = '### select-all ###'
const SPECIAL_NODE_UNSELECT_ALL = '### unselect-all ###'
const SPECIAL_NODE_AGGREGATOR = '### aggregator ###'

const SPECIAL_NODES = [
  SPECIAL_NODE_SELECT_ALL,
  SPECIAL_NODE_UNSELECT_ALL,
  SPECIAL_NODE_AGGREGATOR,
]

export default class TreeMultiselect extends Component {
  constructor(props) {
    super(props)
    const {data, selected} = props
    this.state = {
      data,
      selected,
      currentSelected: selected,
      currentExpanded: {},
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      !isEqual(nextProps.data, prevState.data) ||
      !isEqual(nextProps.selected, prevState.selected)
    ) {
      const {data, selected} = nextProps
      return {data, selected, currentSelected: selected}
    }
    return null
  }

  enhanceNodes = (nodes, underLimit, overLimit) => {
    const {currentSelected, currentExpanded} = this.state
    return nodes.map((node) => {
      const nodeValue = node.value || node.label
      const checked = currentSelected.includes(nodeValue)
      const expanded =
        currentExpanded[nodeValue] === undefined
          ? node.expanded
          : currentExpanded[nodeValue]
      const disabled =
        node.disabled || (checked && underLimit) || (!checked && overLimit)
      const children = this.enhanceNodes(
        node.children || [],
        underLimit,
        overLimit,
      )
      const className = `${node.className || ''}${
        node.unselectable ? ' unselectable' : ''
      }`
      return {
        ...node,
        value: nodeValue,
        checked,
        expanded,
        disabled,
        className,
        children,
      }
    })
  }

  prepareData = () => {
    const {
      texts,
      showSelectAll,
      showUnselectAll,
      minLimit,
      maxLimit,
      aggregatorLimit,
    } = this.props
    const {currentSelected} = this.state
    const preparedData = this.enhanceNodes(
      cloneDeep(this.state.data),
      currentSelected.length <= minLimit,
      maxLimit > 0 && currentSelected.length >= maxLimit,
    )

    if (showUnselectAll && minLimit < 1) {
      preparedData.unshift({
        label: texts.unselectAll || 'Unselect All',
        value: SPECIAL_NODE_UNSELECT_ALL,
        className: 'unselect-all',
        checked: false,
      })
    }
    if (showSelectAll && maxLimit < 1) {
      preparedData.unshift({
        label: texts.selectAll || 'Select All',
        value: SPECIAL_NODE_SELECT_ALL,
        className: 'select-all',
        checked: false,
      })
    }
    if (aggregatorLimit && aggregatorLimit <= currentSelected.length) {
      preparedData.unshift({
        label: `${texts.aggregator || 'Number of selected items: '}${
          currentSelected.length
        }`,
        value: SPECIAL_NODE_AGGREGATOR,
        className: 'aggregator',
        checked: true,
      })
    }

    return preparedData
  }

  getAllValidValues = (nodes) =>
    flatten(
      nodes.map((node) => {
        const childValues = this.getAllValidValues(node.children || [])
        return node.disabled || node.unselectable
          ? childValues
          : [node.value || node.label, ...childValues]
      }),
    )

  getCurrentSelected = (value, selected) => {
    if (value === SPECIAL_NODE_SELECT_ALL) {
      return this.getAllValidValues(this.state.data)
    } else if (value === SPECIAL_NODE_UNSELECT_ALL) {
      return []
    } else {
      return selected
        .filter((i) => !SPECIAL_NODES.includes(i.value) && !i.unselectable)
        .map((i) => i.value)
    }
  }

  onChange = ({value}, selected) => {
    const {setProps} = this.props
    const currentSelected = this.getCurrentSelected(value, selected)
    this.setState({currentSelected})
    setProps({selected: currentSelected})
  }

  onNodeToggle = ({value, expanded}) => {
    const currentExpanded = Object.assign({}, this.state.currentExpanded, {
      [value]: expanded,
    })
    this.setState({currentExpanded})
  }

  render() {
    const {
      data,
      selected,
      setProps,
      className,
      aggregatorLimit,
      ...rest
    } = this.props
    const {currentSelected} = this.state
    const preparedData = this.prepareData()
    const wholeClassName = `${className || ''}${
      aggregatorLimit && aggregatorLimit <= currentSelected.length
        ? ' show-only-aggregator'
        : ''
    }`
    return (
      <DropdownTreeSelect
        data={preparedData}
        className={wholeClassName}
        onChange={this.onChange}
        onNodeToggle={this.onNodeToggle}
        {...rest}
      />
    )
  }
}

TreeMultiselect.defaultProps = {
  mode: 'hierarchical',
  texts: {},
  keepOpenOnSelect: true,
  showSelectAll: false,
  showUnselectAll: false,
  minLimit: 0,
  maxLimit: 0,
  aggregatorLimit: 0,
}

TreeMultiselect.propTypes = {
  /**
   * The ID used to identify this component in Dash callbacks.
   */
  id: PropTypes.string,

  /**
   * Defines how the dropdown is rendered / behaves
   */
  mode: PropTypes.string,

  /**
   * Data for rendering the tree select items
   */
  data: PropTypes.array,

  /**
   * The selected values
   */
  selected: PropTypes.array,

  /**
   * Texts to override various labels, place holders & messages
   */
  texts: PropTypes.object,

  /**
   * Keeps single selects open after selection. Defaults to true
   */
  keepOpenOnSelect: PropTypes.bool,

  /**
   * Minimum number of items to select
   */
  minLimit: PropTypes.number,

  /**
   * Maximum number of items to select
   */
  maxLimit: PropTypes.number,

  /**
   * Minimum number of selected items after which an aggregator is shown
   */
  aggregatorLimit: PropTypes.number,

  /**
   * Show select all node
   */
  showSelectAll: PropTypes.bool,

  /**
   * Show unselect all node
   */
  showUnselectAll: PropTypes.bool,

  /**
   * Dash-assigned callback that should be called to report property changes
   * to Dash, to make them available for callbacks.
   */
  setProps: PropTypes.func,
}
